import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { GameMode, FoundColor, Board, Action, ActionsList } from '@/types/types';
import { secureStorageAdapter } from '../main/utils/storageHelper';

export interface GameState {
  mode: GameMode;
  gameActive: boolean;
  pauseActions: boolean;
  soundEnabled: boolean;
  difficulty: string;
  stars: number;
  foundColor: FoundColor;
  gameData: Record<GameMode, GameData>;
  gameStats: Record<GameMode, GameStats>;
  username: string;
  hasPlayedBefore: boolean;
  unlockedColors: string[];
  wasReset: boolean;
  lastPlayedDate: string | null;
  hydrated: boolean;
  purchasesRestored: boolean;
  setHydrated: () => void;
  setMode: (mode: GameMode) => void;
  setDifficulty: (difficulty: string) => void;
  setPauseActions: (isPaused: boolean) => void;
  increaseStarCount: (count: number) => void;
  setFoundColor: (color: FoundColor) => void;
  setSoundEnabled: (isEnabled: boolean) => void;
  setGameActive: (isActive: boolean) => void;
  increaseMoveCount: () => void;
  updateBoard: (board: Board) => void;
  decreaseSwapCount: () => void;
  addPoints: (points: number) => void;
  addFoundWords: (words: string[]) => void;
  setRecentFoundWords: (words: string[]) => void;
  startTimer: () => void;
  setTimeLeft: (time: number) => void;
  setTimerStartTime: (time: number) => void;
  addTime: (time: number) => void;
  setGameEnded: () => void;
  setStarsAdded: () => void;
  updateStats: () => { newHighScore: boolean; mode: GameMode };
  addAction: (action: Action) => void;
  setSubmittedScore: () => void;
  setUsername: (name: string) => void;
  setHasPlayedBefore: () => void;
  addUnlockedColor: (color: string) => void;
  setWasReset: (resetVal: boolean) => void;
  setLastPlayedDate: (date: string) => void;
  setPurchasesRestored: () => void;
  resetGame: () => void;
}

export interface GameData {
  board: Board;
  swapCount: number;
  moveCount: number;
  points: number;
  foundWords: string[];
  recentFoundWords: string[];
  timeLeft: number;
  timerStarted: boolean;
  timerStartTime: number;
  timerAddedTime: number;
  gameEnded: boolean;
  starsAdded: boolean;
  actionsList: ActionsList;
  submittedScore: boolean;
  gameId: number;
}

export interface GameStats {
  wordsHighScore: number;
  pointsHighScore: number;
}

function generateGameId(): number {
  const array = new Uint32Array(4);
  crypto.getRandomValues(array);
  const idString = Array.from(array, (x) => x.toString().padStart(10, '0'))
    .join('')
    .slice(0, 15);
  return Number(idString);
}

const baseGameState = {
  moveCount: 0,
  points: 0,
  foundWords: [],
  recentFoundWords: [],
  timerStarted: false,
  timerStartTime: new Date().getTime(),
  timerAddedTime: 0,
  gameEnded: false,
  starsAdded: false,
  actionsList: [],
  submittedScore: false,
};

export const setEmptyBoard = (size: number): Board =>
  Array(size)
    .fill(null)
    .map(() => Array(size).fill(''));

const getDefaultGameState = (mode: GameMode): GameData => {
  switch (mode) {
    case 'blitzSimple':
      return {
        ...baseGameState,
        board: setEmptyBoard(4),
        swapCount: 5,
        timeLeft: 3 * 60 * 1001,
        gameId: generateGameId(),
      };
    case 'blitzDeluxe':
      return {
        ...baseGameState,
        board: setEmptyBoard(5),
        swapCount: 8,
        timeLeft: 5 * 60 * 1001,
        gameId: generateGameId(),
      };
    case 'endlessSimple':
      return {
        ...baseGameState,
        board: setEmptyBoard(4),
        swapCount: 5,
        timeLeft: Infinity,
        gameId: generateGameId(),
      };
    case 'endlessDeluxe':
      return {
        ...baseGameState,
        board: setEmptyBoard(5),
        swapCount: 8,
        timeLeft: Infinity,
        gameId: generateGameId(),
      };
  }
};

const getInitialStats = (): GameStats => ({ wordsHighScore: 0, pointsHighScore: 0 });

export const useGameStore = create<GameState>()(
  persist(
    (set, get) => ({
      // Initial state
      mode: 'blitzSimple',
      difficulty: '4x4',
      pauseActions: false,
      stars: 0,
      foundColor: 'green',
      gameActive: false,
      soundEnabled: false,
      username: '',
      hasPlayedBefore: false,
      unlockedColors: [],
      wasReset: false,
      lastPlayedDate: null,
      hydrated: false,
      purchasesRestored: false,
      setHydrated: () => set({ hydrated: true }),
      setPurchasesRestored: () => set({ purchasesRestored: true }),
      gameData: {
        blitzSimple: getDefaultGameState('blitzSimple'),
        blitzDeluxe: getDefaultGameState('blitzDeluxe'),
        endlessSimple: getDefaultGameState('endlessSimple'),
        endlessDeluxe: getDefaultGameState('endlessDeluxe'),
      },
      gameStats: {
        blitzSimple: getInitialStats(),
        blitzDeluxe: getInitialStats(),
        endlessSimple: getInitialStats(),
        endlessDeluxe: getInitialStats(),
      },
      // Setters and game logic
      setMode: (mode) => set({ mode }),
      setDifficulty: (difficulty) => set({ difficulty }),
      setPauseActions: (isPaused) => set({ pauseActions: isPaused }),
      increaseStarCount: (count) => set((state) => ({ stars: state.stars + count })),
      setFoundColor: (color) => set({ foundColor: color }),
      setSoundEnabled: (isEnabled) => set({ soundEnabled: isEnabled }),
      setGameActive: (isActive) => {
        if (!get().hydrated) return;
        set({ gameActive: isActive });
      },
      setUsername: (name) => set({ username: name }),
      setHasPlayedBefore: () => set({ hasPlayedBefore: true }),
      setWasReset: (val) => set({ wasReset: val }),
      setLastPlayedDate: (date) => set({ lastPlayedDate: date }),
      addUnlockedColor: (color) =>
        set((state) => ({ unlockedColors: [...state.unlockedColors, color] })),
      updateBoard: (board) =>
        set((state) => ({
          gameData: {
            ...state.gameData,
            [state.mode]: {
              ...state.gameData[state.mode],
              board,
            },
          },
        })),
      decreaseSwapCount: () =>
        set((state) => ({
          gameData: {
            ...state.gameData,
            [state.mode]: {
              ...state.gameData[state.mode],
              swapCount: state.gameData[state.mode].swapCount - 1,
            },
          },
        })),
      increaseMoveCount: () =>
        set((state) => ({
          gameData: {
            ...state.gameData,
            [state.mode]: {
              ...state.gameData[state.mode],
              moveCount: state.gameData[state.mode].moveCount + 1,
            },
          },
        })),
      addPoints: (points) =>
        set((state) => ({
          gameData: {
            ...state.gameData,
            [state.mode]: {
              ...state.gameData[state.mode],
              points: state.gameData[state.mode].points + points,
            },
          },
        })),
      addFoundWords: (words) =>
        set((state) => ({
          gameData: {
            ...state.gameData,
            [state.mode]: {
              ...state.gameData[state.mode],
              foundWords: state.gameData[state.mode].foundWords.concat(words),
            },
          },
        })),
      setRecentFoundWords: (words) =>
        set((state) => ({
          gameData: {
            ...state.gameData,
            [state.mode]: {
              ...state.gameData[state.mode],
              recentFoundWords: words,
            },
          },
        })),
      startTimer: () =>
        set((state) => ({
          gameData: {
            ...state.gameData,
            [state.mode]: {
              ...state.gameData[state.mode],
              timerStarted: true,
            },
          },
        })),
      setTimeLeft: (time) =>
        set((state) => ({
          gameData: {
            ...state.gameData,
            [state.mode]: {
              ...state.gameData[state.mode],
              timeLeft: time,
            },
          },
        })),
      setTimerStartTime: (time) =>
        set((state) => ({
          gameData: {
            ...state.gameData,
            [state.mode]: {
              ...state.gameData[state.mode],
              timerStartTime: time,
            },
          },
        })),
      addTime: (time) =>
        set((state) => ({
          gameData: {
            ...state.gameData,
            [state.mode]: {
              ...state.gameData[state.mode],
              timerAddedTime: state.gameData[state.mode].timerAddedTime + time,
            },
          },
        })),
      setGameEnded: () =>
        set((state) => ({
          gameData: {
            ...state.gameData,
            [state.mode]: {
              ...state.gameData[state.mode],
              gameEnded: true,
            },
          },
        })),
      setStarsAdded: () =>
        set((state) => ({
          gameData: {
            ...state.gameData,
            [state.mode]: {
              ...state.gameData[state.mode],
              starsAdded: true,
            },
          },
        })),
      updateStats: () => {
        const state = get();
        const mode = state.mode;
        const game = state.gameData[mode];
        const stats = state.gameStats[mode];

        const newHighScore = game.points > stats.pointsHighScore;

        if (newHighScore) {
          set((state) => ({
            gameStats: {
              ...state.gameStats,
              [mode]: {
                pointsHighScore: game.points,
                wordsHighScore: game.foundWords.length,
              },
            },
          }));
        }

        return {
          newHighScore,
          mode,
        };
      },
      setSubmittedScore: () =>
        set((state) => ({
          gameData: {
            ...state.gameData,
            [state.mode]: {
              ...state.gameData[state.mode],
              submittedScore: true,
            },
          },
        })),
      addAction: (action) =>
        set((state) => ({
          gameData: {
            ...state.gameData,
            [state.mode]: {
              ...state.gameData[state.mode],
              actionsList: [...state.gameData[state.mode].actionsList, action],
            },
          },
        })),
      resetGame: () => {
        if (!get().hydrated) return;
        set({
          gameData: {
            blitzSimple: getDefaultGameState('blitzSimple'),
            blitzDeluxe: getDefaultGameState('blitzDeluxe'),
            endlessSimple: getDefaultGameState('endlessSimple'),
            endlessDeluxe: getDefaultGameState('endlessDeluxe'),
          },
          gameActive: false,
        });
      },
    }),
    {
      name: 'game-storage',
      storage: secureStorageAdapter,
      onRehydrateStorage: () => (state) => {
        state?.setHydrated();
      },
      partialize: (state): Partial<GameState> => ({
        gameData: state.gameData,
        gameStats: state.gameStats,
        mode: state.mode,
        difficulty: state.difficulty,
        gameActive: state.gameActive,
        stars: state.stars,
        foundColor: state.foundColor,
        soundEnabled: state.soundEnabled,
        username: state.username,
        hasPlayedBefore: state.hasPlayedBefore,
        unlockedColors: state.unlockedColors,
        wasReset: state.wasReset,
        lastPlayedDate: state.lastPlayedDate,
        purchasesRestored: state.purchasesRestored,
      }),
    }
  )
);
